/* drl-rewards-engine.js — PURE rewards-ladder logic. NO DOM, NO Liquid, NO fetch.

   The portable core of the cart rewards system. evaluate(cart, tiers, options)
   decides which reward stages are reached and which gift lines must be added or
   removed. Because it is side-effect-free, a Shopify Function or a private app
   can reuse it verbatim — keep it that way (math + reconciliation decisions only).

   Contract:
     evaluate(cart, tiers, options) => {
       stages:        [{ id, type, isGift, thresholdCents, label, labelUnlocked, icon, reached, positionPct }],
       next:          { id, label, remainingCents, progressPct } | null,
       qualifyingCents, maxThresholdCents, overallPct, allReached,
       giftsToAdd:    [{ variantId, quantity, tierId }],   // unlocked gifts missing from cart
       giftsToRemove: [{ lineKey, tierId }]                // gift lines whose tier is no longer reached
     }

   cart    = the /cart.js object (or cartData from the cartUpdate pub/sub event)
   tiers   = normalized tier list emitted by the config layer; each:
             { id, type:'free_shipping'|'free_gift', thresholdCents, label,
               labelUnlocked, icon, giftVariantId?, giftQuantity? }
   options.giftMode     = 'stack' (all reached gift tiers, default) | 'highest' (top reached only)
   options.giftProperty = line-item property key marking a gift line (default '_drl_gift') */

(function (root) {
  'use strict';

  var GIFT_PROP = '_drl_gift';

  function isGiftLine(item, prop) {
    return !!(item && item.properties && item.properties[prop]);
  }

  /* Merchandise total that qualifies for the tiers: cart total minus any gift
     lines. Excluding gifts is what stops the add→total-bumps→unlock-next loop,
     and it is robust whether or not the zeroing discount has applied yet. */
  function qualifyingTotal(cart, prop) {
    var total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftLine(items[i], prop)) total -= items[i].final_line_price || 0;
    }
    return total < 0 ? 0 : total;
  }

  function byThresholdAsc(a, b) { return a.thresholdCents - b.thresholdCents; }

  function evaluate(cart, tiers, options) {
    options = options || {};
    var prop = options.giftProperty || GIFT_PROP;
    var giftMode = options.giftMode === 'highest' ? 'highest' : 'stack';

    tiers = (tiers || []).slice().sort(byThresholdAsc);
    var qualifying = qualifyingTotal(cart, prop);

    var maxThreshold = 0;
    var i;
    for (i = 0; i < tiers.length; i++) {
      if (tiers[i].thresholdCents > maxThreshold) maxThreshold = tiers[i].thresholdCents;
    }

    // ── stages + next ──────────────────────────────────────
    var stages = [];
    var next = null;
    for (i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      var reached = qualifying >= t.thresholdCents;
      stages.push({
        id: t.id,
        type: t.type,
        isGift: t.type === 'free_gift',
        thresholdCents: t.thresholdCents,
        label: t.label,
        labelUnlocked: t.labelUnlocked || t.label,
        icon: t.icon,
        reached: reached,
        positionPct: maxThreshold > 0 ? Math.round((t.thresholdCents / maxThreshold) * 100) : 0
      });
      if (!reached && next === null) {
        next = {
          id: t.id,
          label: t.label,
          remainingCents: t.thresholdCents - qualifying,
          progressPct: t.thresholdCents > 0 ? Math.min(100, Math.round((qualifying / t.thresholdCents) * 100)) : 100
        };
      }
    }

    var overallPct = maxThreshold > 0 ? Math.min(100, Math.round((qualifying / maxThreshold) * 100)) : 0;
    var allReached = tiers.length > 0 && next === null;

    // ── gift reconciliation ────────────────────────────────
    // Which gift tiers are currently earned…
    var reachedGifts = [];
    for (i = 0; i < tiers.length; i++) {
      if (tiers[i].type === 'free_gift' && tiers[i].giftVariantId && qualifying >= tiers[i].thresholdCents) {
        reachedGifts.push(tiers[i]);
      }
    }
    // 'highest' keeps only the top reached gift tier; 'stack' keeps them all.
    var wanted = reachedGifts;
    if (giftMode === 'highest' && reachedGifts.length > 1) {
      wanted = [reachedGifts[reachedGifts.length - 1]];
    }
    var wantedById = {};
    for (i = 0; i < wanted.length; i++) wantedById[wanted[i].id] = true;

    // …vs. which gift lines are already in the cart (keyed by the tier id we stored).
    var presentByTier = {};
    var items = (cart && cart.items) || [];
    for (i = 0; i < items.length; i++) {
      if (!isGiftLine(items[i], prop)) continue;
      presentByTier[String(items[i].properties[prop])] = items[i].key;
    }

    var giftsToAdd = [];
    for (i = 0; i < wanted.length; i++) {
      if (!Object.prototype.hasOwnProperty.call(presentByTier, wanted[i].id)) {
        giftsToAdd.push({ variantId: wanted[i].giftVariantId, quantity: wanted[i].giftQuantity || 1, tierId: wanted[i].id });
      }
    }
    var giftsToRemove = [];
    for (var tierId in presentByTier) {
      if (!Object.prototype.hasOwnProperty.call(presentByTier, tierId)) continue;
      if (!wantedById[tierId]) giftsToRemove.push({ lineKey: presentByTier[tierId], tierId: tierId });
    }

    return {
      stages: stages,
      next: next,
      qualifyingCents: qualifying,
      maxThresholdCents: maxThreshold,
      overallPct: overallPct,
      allReached: allReached,
      giftsToAdd: giftsToAdd,
      giftsToRemove: giftsToRemove
    };
  }

  var api = { evaluate: evaluate, GIFT_PROP: GIFT_PROP };
  root.DrlRewards = api;
  /* node-importable so the engine can be unit-tested without a browser */
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : this);
